/**
 * Custom routes with special guards in the render function
 */
import React, { useContext, useEffect } from 'react';
import { Route, Redirect } from 'react-router-dom';
import AppContext from '../../contexts/AppContext';
import AuthenticationContext from '../../contexts/AuthenticationContext';
import RolesContext from '../../contexts/RolesContext';
import Loading from '../Shared/ResourceIndex/Loading';
import { env } from '../../config';
import { useOffline } from '../../contexts/OfflineContext';
import { FormContextProvider } from '@sw-sw/lib-form';
import PublicInfoModal from '../PublicUrl/Component/PublicInfoModal';

/**
 * Private route. Only accessible by logged-in user.
 */
export const PrivateRoute = (routeProps) => {
  const { Component, Layout, checking, ...rest } = routeProps;
  const appStore = useContext(AppContext);
  const authStore = useContext(AuthenticationContext);
  const rolesStore = useContext(RolesContext);
  const user = appStore.get('user');

  if (!appStore.initialDataLoaded) {
    return <Loading />;
  }

  return (
    <Route
      exact
      {...rest}
      render={(props) =>
        authStore.loggedIn &&
        rolesStore.navigationIsAllowed(props.location.pathname) ? (
          <Layout user={user} checking={checking} {...props}>
            <Component user={user} {...props} />
          </Layout>
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              search: `?to=${props.location.pathname}${props.location.search}`,
              state: { from: props.location.pathname },
            }}
          />
        )
      }
    />
  );
};

export const PublicUrlRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;
  const authStore = useContext(AuthenticationContext);
  const [showPublicInfoModal, setShowPublicInfoModal] = React.useState(false);
  // Token is now in HTTP-only secure cookie, no need to check sessionStorage
  const hasImage = routeProps.location.search.startsWith('?image');

  const getTenant = async (name) => {
    try {
      const tenantData = await authStore.getTenantByName(name);

      if (!tenantData) {
        window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
      }

      return tenantData;
    } catch (error) {
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
    }
  };

  const performLogin = async (publicUserData) => {
    const { host, origin, pathname } = window.location;

    // Only run for subdomains & non-login origin
    if (
      host.split('.').length <= 1 ||
      origin === process.env.REACT_APP_LOGIN_URL
    ) {
      return;
    }

    const tenantName = host.split('.')[0];
    const username = hasImage
      ? env.REACT_APP_USERNAME_NO_IMAGE
      : env.REACT_APP_USERNAME;

    try {
      const tenantData = await getTenant(tenantName);
      const projectId = pathname.includes('projects/')
        ? pathname.split('projects/')[1]?.split('/')[0]
        : null;

      if (tenantData) {
        await authStore.publiclogin(
          username,
          env.REACT_APP_PASSWORD,
          tenantData.domain,
          {
            publicUserName: publicUserData.name,
            publicUserEmail: publicUserData.email,
            publicUserCompany: publicUserData.company,
            publicLink: `${origin}${pathname}`,
            projectId,
          },
        );
      } else {
        if (!projectId) {
          window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
          return;
        }
        await authStore.publicLoginWithProject(
          username,
          env.REACT_APP_PASSWORD,
          projectId,
          {
            publicUserName: publicUserData.name,
            publicUserEmail: publicUserData.email,
            userCompany: publicUserData.company,
            publicLink: `${origin}${pathname}`,
            projectId,
          },
        );
      }
    } catch (err) {
      console.error('Public login failed', err);
    } finally {
      setShowPublicInfoModal(false);
      window.location.reload();
    }
  };

  useEffect(async () => {
    if (authStore.loggedIn) {
      setShowPublicInfoModal(false);
      return;
    }

    if (!authStore.loggedIn) {
      setShowPublicInfoModal(true);
      return;
    }
  }, [authStore.loggedIn]);

  const handlePublicInfoSubmit = (data) => {
    performLogin(data);
  };

  const handlePublicInfoCancel = () => {
    // Redirect to login page if user cancels
    window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
  };

  return (
    <>
      {showPublicInfoModal ? (
        <FormContextProvider initialValue={{}}>
          <PublicInfoModal
            onSubmit={handlePublicInfoSubmit}
            onCancel={handlePublicInfoCancel}
          />
        </FormContextProvider>
      ) : (
        <Route
          exact
          {...rest}
          render={(props) => (
            <Layout {...props}>
              <Component {...props} />
            </Layout>
          )}
        />
      )}
    </>
  );
};

/**
 * Public route. Only acessible by annon/guest user
 */
export const PublicRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;
  const authStore = useContext(AuthenticationContext);

  const isOffline = useOffline();

  /** When logged in, force logout, before rendering */
  useEffect(() => {
    if (authStore.loggedIn && !isOffline) {
      authStore.logout();
    }
  }, []);

  return (
    <Route
      exact
      {...rest}
      render={(props) => (
        <Layout {...props}>
          <Component {...props} />
        </Layout>
      )}
    />
  );
};

/**
 * Generic route. Accesible by anyone.
 */
export const GenericRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;

  return (
    <Route
      exact
      {...rest}
      render={(props) => (
        <Layout {...props}>
          <Component {...props} />
        </Layout>
      )}
    />
  );
};

export const GuestUrlRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;
  const authStore = useContext(AuthenticationContext);
  // Token is now in HTTP-only secure cookie, no need to check sessionStorage

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    if (
      window.location.host.split('.').length > 1 &&
      window.location.origin !== process.env.REACT_APP_LOGIN_URL
    ) {
      const tenantName = window.location.host.split('.')[0];

      const tenantData = await getTenant(tenantName);

      // Always attempt login since token is in HTTP-only cookie
      if (tenantData) {
        authStore
          .guestlogin(
            env.REACT_APP_GUEST_USER_EMAIL,
            env.REACT_APP_GUEST_USER_PASSWORD,
            tenantData.domain,
          )
          .then((res) => {
            console.log('Guest login success');
          });
      }
    }
  }, [window.location.host]);

  const getTenant = async (name) => {
    try {
      const tenantData = await authStore.getTenantByName(name);

      if (!tenantData) {
        window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
      }

      return tenantData;
    } catch (error) {
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
    }
  };

  return (
    <Route
      exact
      {...rest}
      render={(props) => (
        <Layout {...props}>
          <Component {...props} />
        </Layout>
      )}
    />
  );
};

export { DivisionRoute } from './DivisionRoute';
export { RedirectRoute } from './RedirectRoute';
